import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DashboardTile } from './DashboardTile';
import { tokens } from '../../../design';

/**
 * Shared frame for the four analytics tiles (GMV, order-structure donut,
 * online-now, orders-by-hour). Renders the uppercase label + optional
 * right-hand slot (a delta badge), and centralises the per-tile
 * loading / error states so each tile only implements its data body.
 *
 * `dark` switches to the mockup's dark-accent surface (`.t-online`).
 * The data `children` render whenever there's no load/error — a zero-
 * filled series is valid data and renders a flat chart, NOT an error
 * (guardrail: zero series → flat, not a crash).
 */
export interface AnalyticsTileProps {
  label: string;
  gridColumn: string;
  gridRow?: string | undefined;
  /** Right-hand header slot (e.g. a delta badge). */
  headerSlot?: ReactNode;
  isLoading: boolean;
  isError: boolean;
  hasData: boolean;
  dark?: boolean;
  children: ReactNode;
}

export const AnalyticsTile = ({
  label,
  gridColumn,
  gridRow,
  headerSlot,
  isLoading,
  isError,
  hasData,
  dark = false,
  children,
}: AnalyticsTileProps): ReactElement => {
  const { t } = useTranslation();
  const labelColor = dark ? 'rgba(255,255,255,0.5)' : tokens.text.tertiary;

  return (
    <DashboardTile
      role="group"
      aria-label={label}
      sx={{
        gridColumn,
        gridRow,
        ...(dark
          ? { background: 'linear-gradient(135deg, #0e1c14 0%, #0a1610 100%)', color: '#fff' }
          : {}),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: labelColor,
          }}
        >
          {label}
        </Typography>
        {!isLoading && !isError && hasData ? headerSlot : null}
      </Box>

      {isLoading ? (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 2 }}
        >
          <CircularProgress size={20} />
        </Box>
      ) : null}

      {isError || (!isLoading && !hasData) ? (
        <Typography
          variant="body2"
          sx={{ color: dark ? 'rgba(255,255,255,0.7)' : tokens.state.danger, py: 2 }}
        >
          {t('dashboard.loadError')}
        </Typography>
      ) : null}

      {!isLoading && !isError && hasData ? children : null}
    </DashboardTile>
  );
};
